// web-commands.ts — the PER-APP half of the web shape: maps expense_audit_ai's Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD + the dashboard rollup are ported faithfully from db.rs so the UI behaves the same.
//
// Photos: on native the receipt image is copied into receipts/ and referenced by path; on web
// there is no filesystem, so a pasted receipt is kept as a data: URL (rendered as-is via the
// identity convertFileSrc in tauri-web.ts). OCR: native uses macOS Vision, which can't run in a
// browser, so ocr_image is a web no-op (empty string) — the UI already falls back to "paste the
// text or fill the form" when OCR returns nothing.

import type { CommandHandler, Row, WebStore } from "./web-runtime";
import { browserSearch, browserFetch } from "./web-search-browser";

const EXPENSES = "expenses";
const CATEGORIES = "categories";
const AUDIT = "audit";
const RECURRING = "recurring";
const BUDGETS = "budgets";

// Date helpers for the recurring engine. Rust uses chrono::NaiveDate — timezone-free — so the
// faithful browser mirror is UTC arithmetic. Using local dates here would drift a day either
// side of midnight and silently add a rent entry on the wrong date.
const dayMs = 86_400_000;
const toUtc = (iso: string): number => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
const isoOf = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

// Mirrors DEFAULT_CATEGORIES in db.rs (name, color).
const DEFAULT_CATEGORIES: [string, string][] = [
  ["Groceries", "#10B981"],
  ["Meals", "#F59E0B"],
  ["Transport", "#3B82F6"],
  ["Home & bills", "#8B5CF6"],
  ["Shopping", "#EC4899"],
  ["Health", "#EF4444"],
  ["Fun", "#0EA5E9"],
  ["Subscriptions", "#14B8A6"],
  ["Travel", "#2563EB"],
  ["Software", "#7C3AED"],
  ["Office", "#D97706"],
  ["Marketing", "#DC2626"],
  ["Hardware", "#0891B2"],
  ["Other", "#6B7280"],
];

function uuid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}

function catsById(db: WebStore): Map<string, Row> {
  return new Map(db.all(CATEGORIES).map((c) => [String(c.id), c]));
}

// Reproduce the LEFT JOIN categories that db.rs adds to every expense row.
function joinExpense(row: Row, cats: Map<string, Row>): Row {
  const cat = row.category_id != null ? cats.get(String(row.category_id)) : undefined;
  return {
    ...row,
    category_name: cat ? cat.name : null,
    category_color: cat ? cat.color : null,
  };
}

// expenses sort: date_on DESC, then created_at DESC (matches db.rs ORDER BY).
function byDateDesc(a: Row, b: Row): number {
  const d = String(b.date_on).localeCompare(String(a.date_on));
  return d !== 0 ? d : String(b.created_at).localeCompare(String(a.created_at));
}

function logAudit(db: WebStore, expenseId: string | null, action: string, detail: string | null): void {
  db.insert(AUDIT, { id: uuid(), expense_id: expenseId, action, detail, at: nowIso() });
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Seed ────────────────────────────────────────────────────────────────
    ensure_seed: () => {
      if (db.all(CATEGORIES).length > 0) return null;
      for (const [name, color] of DEFAULT_CATEGORIES) {
        db.insert(CATEGORIES, { id: uuid(), name, color, is_default: true, created_at: nowIso() });
      }
      return null;
    },

    // ── Categories ────────────────────────────────────────────────────────────
    list_categories: () => {
      const rows = db.all(CATEGORIES);
      rows.sort((a, b) => {
        const ad = a.is_default ? 1 : 0;
        const bd = b.is_default ? 1 : 0;
        if (ad !== bd) return bd - ad; // is_default DESC
        return String(a.name).localeCompare(String(b.name)); // name ASC
      });
      return rows;
    },
    create_category: ({ name, color }) => {
      const n = String(name ?? "").trim();
      if (!n) throw new Error("Category name required");
      const c = String(color ?? "").trim();
      const row: Row = { id: uuid(), name: n, color: c || "#6B7280", is_default: false, created_at: nowIso() };
      return db.insert(CATEGORIES, row);
    },
    update_category: ({ id, name, color }) => {
      const cat = db.get(CATEGORIES, String(id));
      if (!cat) throw new Error("Category not found");
      const patch: Partial<Row> = {};
      if (name != null) {
        const n = String(name).trim();
        if (!n) throw new Error("Category name required");
        patch.name = n;
      }
      if (color != null) {
        const c = String(color).trim();
        if (c) patch.color = c;
      }
      db.update(CATEGORIES, String(id), patch);
      return db.get(CATEGORIES, String(id));
    },
    delete_category: ({ id }) => {
      const cat = db.get(CATEGORIES, String(id));
      if (!cat) throw new Error("Category not found");
      if (cat.is_default) throw new Error("Cannot delete a default category");
      // Detach the category from any expenses using it (mirrors the UPDATE …SET category_id=NULL).
      for (const e of db.all(EXPENSES)) {
        if (e.category_id === id) db.update(EXPENSES, String(e.id), { category_id: null, updated_at: nowIso() });
      }
      db.remove(CATEGORIES, String(id));
      return null;
    },

    // ── Expenses ────────────────────────────────────────────────────────────
    list_expenses: ({ status, categoryId, limit }) => {
      const cats = catsById(db);
      let rows = db.all(EXPENSES);
      if (status) rows = rows.filter((r) => r.status === status);
      if (categoryId) rows = rows.filter((r) => r.category_id === categoryId);
      rows.sort(byDateDesc);
      const lim = typeof limit === "number" ? limit : 50000;
      return rows.slice(0, lim).map((r) => joinExpense(r, cats));
    },
    get_expense: ({ id }) => {
      const row = db.get(EXPENSES, String(id));
      return row ? joinExpense(row, catsById(db)) : null;
    },
    create_expense: ({ input }) => {
      const i = (input as Record<string, unknown>) ?? {};
      const vendor = String(i.vendor ?? "").trim();
      if (!vendor) throw new Error("Vendor is required");
      const amount = Number(i.amount_cents ?? 0);
      if (amount < 0) throw new Error("Amount cannot be negative");
      const now = nowIso();
      const notes = i.notes == null ? null : String(i.notes).trim() || null;
      const row: Row = {
        id: uuid(),
        category_id: (i.category_id as string | null) ?? null,
        vendor,
        amount_cents: amount,
        currency: (i.currency as string) || "USD",
        date_on: String(i.date_on ?? ""),
        status: (i.status as string) || "pending",
        notes,
        ai_confidence: (i.ai_confidence as number | null) ?? null,
        receipt_path: (i.receipt_path as string | null) ?? null,
        created_at: now,
        updated_at: now,
      };
      db.insert(EXPENSES, row);
      logAudit(db, row.id, "created", i.ai_confidence != null ? "ai-assisted" : "manual");
      return joinExpense(row, catsById(db));
    },
    update_expense: ({ id, patch }) => {
      const existing = db.get(EXPENSES, String(id));
      if (!existing) throw new Error("Expense not found");
      const p = (patch as Record<string, unknown>) ?? {};
      const merged: Partial<Row> = { updated_at: nowIso() };
      // Present = set; absent = leave unchanged (mirrors ExpensePatch semantics in db.rs).
      if (p.vendor !== undefined) {
        const v = String(p.vendor).trim();
        if (v) merged.vendor = v; // empty vendor keeps the existing value
      }
      if (p.amount_cents !== undefined) merged.amount_cents = Number(p.amount_cents);
      if (p.currency !== undefined) merged.currency = String(p.currency);
      if (p.date_on !== undefined) merged.date_on = String(p.date_on);
      if (p.status !== undefined) merged.status = String(p.status);
      if (p.category_id !== undefined) {
        const t = String(p.category_id).trim();
        merged.category_id = t ? t : null; // "" clears to NULL
      }
      if (p.notes !== undefined) {
        const t = String(p.notes).trim();
        merged.notes = t ? t : null; // "" clears to NULL
      }
      db.update(EXPENSES, String(id), merged);
      const action = p.status !== undefined && String(p.status) !== existing.status ? String(p.status) : "updated";
      logAudit(db, String(id), action, null);
      return null;
    },
    delete_expense: ({ id }) => {
      db.remove(EXPENSES, String(id));
      logAudit(db, String(id), "deleted", null);
      return null;
    },

    // ── Recurring ───────────────────────────────────────────────────────────
    // These four were MISSING from the web mirror while the chat brain advertised
    // create/list/delete_recurring as tools. On web the user said "rent 1200 every month on
    // the 1st", the billed AI call ran, and THEN the command threw — a charge with an error
    // for the customer. Ported from db.rs (create/list/delete/materialize_recurring).
    list_recurring: () => {
      // Same ranking as db.rs: what it costs you in a YEAR, biggest first. Insertion order
      // answers no question the user is asking of this list.
      const annual = (r: Row) => Number(r.amount_cents ?? 0) * (r.frequency === "weekly" ? 52 : 12);
      return db.all(RECURRING).slice().sort(
        (a, b) => annual(b) - annual(a) || String(a.created_at).localeCompare(String(b.created_at)),
      );
    },
    create_recurring: ({ vendor, amountCents, currency, categoryId, notes, frequency, day }) => {
      const v = String(vendor ?? "").trim();
      if (!v) throw new Error("Vendor is required");
      const amount = Number(amountCents ?? 0);
      if (amount <= 0) throw new Error("Amount must be positive");
      const weekly = String(frequency) === "weekly";
      const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));
      // monthly clamps to 1..28 so the day exists in EVERY month (mirrors db.rs).
      const d = weekly ? clamp(Number(day ?? 0), 0, 6) : clamp(Number(day ?? 1), 1, 28);
      const row: Row = {
        id: uuid(),
        vendor: v,
        amount_cents: amount,
        currency: (currency as string) || "USD",
        category_id: (categoryId as string | null) ?? null,
        notes: notes == null ? null : String(notes),
        frequency: weekly ? "weekly" : "monthly",
        day: d,
        last_added_on: null,
        created_at: nowIso(),
      };
      db.insert(RECURRING, row);
      logAudit(db, null, "recurring-created", `${v} ${row.frequency} day ${d}`);
      return row;
    },
    delete_recurring: ({ id }) => {
      db.remove(RECURRING, String(id));
      logAudit(db, null, "recurring-deleted", String(id));
      return null;
    },
    materialize_recurring: ({ today }) => {
      const todayMs = toUtc(String(today ?? ""));
      if (Number.isNaN(todayMs)) return 0;
      let added = 0;
      for (const r of db.all(RECURRING)) {
        const startIso = (r.last_added_on as string | null) ?? String(r.created_at).slice(0, 10);
        const startMs = toUtc(startIso);
        if (Number.isNaN(startMs)) continue;
        let last: string | null = null;
        // guard 800 — same backstop as db.rs, so a rule created years ago can't spin forever.
        let cursor = startMs + dayMs;
        for (let guard = 0; cursor <= todayMs && guard < 800; guard++, cursor += dayMs) {
          const dt = new Date(cursor);
          const hit = r.frequency === "weekly"
            ? dt.getUTCDay() === Number(r.day)
            : dt.getUTCDate() === Number(r.day);
          if (!hit) continue;
          const ds = isoOf(cursor);
          const now = nowIso();
          const row: Row = {
            id: uuid(),
            category_id: (r.category_id as string | null) ?? null,
            vendor: String(r.vendor),
            amount_cents: Number(r.amount_cents ?? 0),
            currency: String(r.currency ?? "USD"),
            date_on: ds,
            status: "pending",
            notes: "recurring",
            ai_confidence: null,
            receipt_path: null,
            created_at: now,
            updated_at: now,
          };
          db.insert(EXPENSES, row);
          logAudit(db, row.id, "created", "manual");
          added++;
          last = ds;
        }
        if (last) db.update(RECURRING, String(r.id), { last_added_on: last });
      }
      return added;
    },

    // ── Dashboard ──────────────────────────────────────────────────────────
    dashboard_summary: ({ monthStart }) => {
      const ms = String(monthStart ?? "");
      const cats = catsById(db);
      const inMonth = db.all(EXPENSES).filter((e) => String(e.date_on) >= ms);
      const month_total_cents = inMonth.reduce((s, e) => s + Number(e.amount_cents ?? 0), 0);
      const month_count = inMonth.length;

      const groups = new Map<string, { category_id: string | null; total: number; count: number }>();
      for (const e of inMonth) {
        const cid = (e.category_id as string | null) ?? null;
        const key = cid ?? "__none__";
        const g = groups.get(key) ?? { category_id: cid, total: 0, count: 0 };
        g.total += Number(e.amount_cents ?? 0);
        g.count += 1;
        groups.set(key, g);
      }
      const by_category = [...groups.values()]
        .map((g) => {
          const cat = g.category_id ? cats.get(g.category_id) : undefined;
          return {
            category_id: g.category_id,
            category_name: cat ? cat.name : null,
            category_color: cat ? cat.color : null,
            total_cents: g.total,
            count: g.count,
          };
        })
        .sort((a, b) => b.total_cents - a.total_cents);

      const recent = db.all(EXPENSES).sort(byDateDesc).slice(0, 5).map((r) => joinExpense(r, cats));
      return { month_total_cents, month_count, by_category, recent };
    },

    // ── Budgets + breakdown (mirrors db.rs; all local arithmetic) ───────────
    set_budget: ({ categoryId, amountCents }) => {
      const amount = Number(amountCents ?? 0);
      if (amount <= 0) throw new Error("Budget must be more than zero");
      const cid = (categoryId as string | null) ?? null;
      // Upsert on category — setting a budget twice edits it, never duplicates.
      const existing = db.all(BUDGETS).find((b) => ((b.category_id as string | null) ?? null) === cid);
      if (existing) {
        db.update(BUDGETS, String(existing.id), { amount_cents: amount, updated_at: nowIso() });
        return db.get(BUDGETS, String(existing.id));
      }
      const at = nowIso();
      return db.insert(BUDGETS, { id: uuid(), category_id: cid, amount_cents: amount, created_at: at, updated_at: at });
    },
    delete_budget: ({ id }) => {
      db.remove(BUDGETS, String(id));
      return null;
    },
    budget_status: ({ monthStart }) => {
      const prefix = String(monthStart ?? "").slice(0, 7);
      const cats = catsById(db);
      const inMonth = db.all(EXPENSES).filter((e) => String(e.date_on).slice(0, 7) === prefix);
      return db
        .all(BUDGETS)
        .map((b) => {
          const cid = (b.category_id as string | null) ?? null;
          const spent = inMonth
            .filter((e) => cid === null || e.category_id === cid)
            .reduce((s, e) => s + Number(e.amount_cents ?? 0), 0);
          const amount = Number(b.amount_cents ?? 0);
          const cat = cid ? cats.get(cid) : undefined;
          const pct = amount > 0 ? Math.round((spent / amount) * 100) : 0;
          return {
            id: String(b.id),
            category_id: cid,
            category_name: cat ? String(cat.name) : null,
            category_color: cat ? String(cat.color) : null,
            amount_cents: amount,
            spent_cents: spent,
            remaining_cents: amount - spent,
            percent_used: Math.min(100, Math.max(0, pct)),
          };
        })
        // Overall limit first, then categories by name — same ORDER BY as db.rs.
        .sort((a, b) =>
          (a.category_id === null ? 0 : 1) - (b.category_id === null ? 0 : 1) ||
          (a.category_name ?? "").localeCompare(b.category_name ?? ""),
        );
    },
    spend_trend: ({ months }) => {
      const limit = Math.min(24, Math.max(1, Number(months ?? 6)));
      const groups = new Map<string, { total: number; count: number }>();
      for (const e of db.all(EXPENSES)) {
        const m = String(e.date_on ?? "").slice(0, 7);
        if (!m) continue;
        const g = groups.get(m) ?? { total: 0, count: 0 };
        g.total += Number(e.amount_cents ?? 0);
        g.count += 1;
        groups.set(m, g);
      }
      return [...groups.entries()]
        .map(([month, g]) => ({ month, total_cents: g.total, count: g.count }))
        .sort((a, b) => b.month.localeCompare(a.month))
        .slice(0, limit);
    },
    spend_by_category: ({ from, to }) => {
      const f = String(from ?? ""), t = String(to ?? "");
      const cats = catsById(db);
      const rows = db.all(EXPENSES).filter((e) => String(e.date_on) >= f && String(e.date_on) <= t);
      const groups = new Map<string, { category_id: string | null; total: number; count: number }>();
      for (const e of rows) {
        const cid = (e.category_id as string | null) ?? null;
        const g = groups.get(cid ?? "__none__") ?? { category_id: cid, total: 0, count: 0 };
        g.total += Number(e.amount_cents ?? 0);
        g.count += 1;
        groups.set(cid ?? "__none__", g);
      }
      return [...groups.values()]
        .map((g) => {
          const cat = g.category_id ? cats.get(g.category_id) : undefined;
          return {
            category_id: g.category_id,
            category_name: cat ? cat.name : null,
            category_color: cat ? cat.color : null,
            total_cents: g.total,
            count: g.count,
          };
        })
        .sort((a, b) => b.total_cents - a.total_cents);
    },

    // ── Audit ─────────────────────────────────────────────────────────────────
    list_audit: ({ expenseId, limit }) => {
      let rows = db.all(AUDIT);
      if (expenseId) rows = rows.filter((r) => r.expense_id === expenseId);
      rows.sort((a, b) => String(b.at).localeCompare(String(a.at)));
      const lim = typeof limit === "number" ? limit : 100;
      return rows.slice(0, lim);
    },

    // ── Receipt images ─────────────────────────────────────────────────────────
    // Native copies a picked/dropped file into receipts/ and returns its path. On web there is no
    // filesystem path to copy from, so echo the ref back (data:/blob: URLs render as-is via convertFileSrc).
    save_receipt_image: ({ srcPath }) => String(srcPath ?? ""),
    // Native writes pasted clipboard bytes into receipts/. On web, keep the bytes as a data: URL.
    save_pasted_image: ({ bytes, ext }) => {
      const arr = Uint8Array.from((bytes as number[]) ?? []);
      let bin = "";
      for (let i = 0; i < arr.length; i++) bin += String.fromCharCode(arr[i]);
      const raw = String(ext ?? "png").toLowerCase();
      const kind = raw === "jpg" ? "jpeg" : raw;
      return `data:image/${kind};base64,${btoa(bin)}`;
    },
    // OCR: native uses macOS Vision. A browser can't run Vision, so this is a no-op — the UI
    // already handles an empty result by asking the user to paste the text or fill the form.
    // TODO: web OCR via a browser lib (e.g. tesseract.js) or a server endpoint.
    ocr_image: () => "",

    // ── Platform shims used elsewhere in the UI ─────────────────────────────────
    // No native deep-link buffer on web.
    take_pending_deep_link: () => null,
    // Native opens an in-app WebKit window to delete the account. On web (same-origin as the site),
    // navigate the browser to the same delete page carrying the session tokens.
    open_delete_window: ({ accessToken, refreshToken }) => {
      window.location.href = `https://orioralabs.com/account/delete#access_token=${String(accessToken ?? "")}&refresh_token=${String(refreshToken ?? "")}`;
      return null;
    },
  };
}
